import * as React from "react";
import {
  Badge,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@finster/ui";
import { Clock } from "lucide-react";
import type { Tab } from "../types";
import {
  chatRowLabel,
  chatScopeLabel,
  chatsWithMessages,
  recentChats,
} from "./chatTabLabels";

const menuItemClass =
  "min-h-8 cursor-pointer rounded-md px-2 py-[5.5px] text-[14px] font-medium leading-5 hover:bg-accent data-[highlighted]:bg-accent";

interface ChatHistoryDropdownMenuProps {
  sortedTabs: readonly Tab[];
  activeTabId?: string;
  onSelectTab: (tabId: string) => void;
  onViewAllChats: () => void;
}

export const ChatHistoryDropdownMenu: React.FC<ChatHistoryDropdownMenuProps> = ({
  sortedTabs,
  activeTabId,
  onSelectTab,
  onViewAllChats,
}) => {
  const recents = recentChats(sortedTabs);
  const tabCount = chatsWithMessages(sortedTabs).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open chat history"
          className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2"
        >
          <Clock className="pointer-events-none h-4 w-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[min(277px,calc(100vw-1.5rem))] rounded-lg p-0.5"
      >
        <DropdownMenuLabel className="px-2 py-1 text-[12px] font-medium leading-[18px] text-muted-foreground">
          Recent Chats ({tabCount})
        </DropdownMenuLabel>
        {recents.length > 0 && (
          <DropdownMenuGroup className="flex flex-col gap-0.5">
            {recents.map((tab) => {
              const isActive = tab.id === activeTabId;
              const label = chatRowLabel(tab);
              return (
                <DropdownMenuItem
                  key={tab.id}
                  title={label}
                  onSelect={() => onSelectTab(tab.id)}
                  className={cn(menuItemClass, "gap-2", isActive && "bg-muted")}
                >
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                  <Badge
                    variant="secondary"
                    className="shrink-0 px-2 py-0.5 text-[12px] font-medium leading-[18px] text-foreground shadow-none"
                  >
                    {chatScopeLabel(tab)}
                  </Badge>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            onSelect={() => onViewAllChats()}
            className={menuItemClass}
          >
            View all chats
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
